import { ChangeEvent, FC, JSX, useContext, useEffect, useMemo, useState } from "react";

import { Breadcrumbs, LoadingSpinner, Page, PageHeader, PageHeaderRight, PageInner, PageSection } from "@app/shared";
import { QESSelect, QESTextInput } from "../shared/qes-standards";
import { ReportsPanel } from "./ReportsPanel";
import { AnalysisTypeConverter, ProjectTypeConverter, ProviderCategoryConverter, ReportFileTypeConverter, ReportStatusConverter, SpecialtyFilterConverter } from "./reportsModel";
import { Roles, UserContext } from "@app/user";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { Unauthorized } from "../shared/ErrorPages";
import { ReportsContext, ReportsContextProvider } from "./ReportsContext";
import { RouteComponentProps } from "react-router-dom";
import { preventDefaultMouseEvent } from "../shared/utility";
import { compact } from "lodash";
import { ResourceFiltrationService } from "@app/projects/resource-filtration";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { FeatureFlagContext, Features } from "../feature-flags";

enum ActiveReportTab {
  YourReports = "Your Reports",
  SharedReports = "Shared Reports",
}

const Reports: FC<React.PropsWithChildren<RouteComponentProps>> = (props: RouteComponentProps) => {
  const userInfo = useContext(UserContext);
  const resourceFiltrationService = new ResourceFiltrationService();
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  if (userInfo.roles.includes(Roles.SOFTWARE_LICENSE)) {
    return <Unauthorized />;
  }

  const context = useContext(ReportsContext);
  const [activeReportTab, setActiveReportTab] = useState(ActiveReportTab.YourReports);
  const [showPciVolumeReportingOption, setShowPciVolumeReportingOption] = useState<boolean>(false);

  useEffect(() => {
    const query = new URLSearchParams(props.location.search);
    if (query.get("rid")) {
      context.downloadFile(parseInt(query.get("pid"), 10), parseInt(query.get("rid"), 10));
    }
  }, [props.location.search]);

  useEffect(() => {
    getPciVolumeReportingStatus();
  }, []);

  const searchValueField = (): JSX.Element => {
    const inputProps = {
      containerClassName: "col-4",
      label: "Search For:",
      onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => context.setSearchValue(e.target.value),
      testId: "reports__search-value",
      value: context.searchValue,
    };
    switch (context.searchType) {
      case "As Of":
      case "Last Requested":
        const todaysDate = new Date().toISOString().slice(0, -14);
        let thirtyString;
        // last requested is the create date of the report, so it has to be in the last 30 days
        // as of is the run configuration date, which could be older
        if (context.searchType === "Last Requested") {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setHours(-24 * 30);
          thirtyString = thirtyDaysAgo.toISOString().slice(0, -14);
        }
        return (
          <>
            <QESDateInput {...inputProps} label="Start Date" containerClassName="col-3" max={context.searchValue2 || todaysDate} min={thirtyString} />
            <QESDateInput
              containerClassName="col-3"
              value={context.searchValue2}
              max={todaysDate}
              min={context.searchValue || thirtyString}
              label="End Date"
              testId="reports__search-value-2"
              onChange={(e: ChangeEvent<HTMLInputElement>) => context.setSearchValue2(e.target.value)}
            />
          </>
        );
      case "ProjectName":
      case "ReportName":
        return <QESTextInput {...inputProps} />;
      case "Project Type":
        return (
          <QESSelect {...inputProps}>
            {Object.entries(ProjectTypeConverter).map((entry) => (
              context.availableFilterCriteria?.availableProjectTypes.includes(entry[0]) && <option value={entry[0]} key={entry[0]}>{entry[1]}</option>)
            )}
          </QESSelect>
        );
      case "Format":
        return (
          <QESSelect {...inputProps}>
            {Object.entries(ReportFileTypeConverter).map((entry) => {
              return context.availableFilterCriteria?.availableReportTypes.includes(entry[0]) && <option value={entry[0]} key={entry[0]}>{entry[1]}</option>;
            })}
          </QESSelect>
        );
      case "Provider Category":
        return (
          <QESSelect {...inputProps}>
            {Object.entries(ProviderCategoryConverter).map((entry) => {
              return context.availableFilterCriteria?.availableProviderCategories.includes(entry[0]) && <option value={entry[0]} key={entry[0]}>{entry[1]}</option>;
            })}
          </QESSelect>
        );
      case "Specialty Filter":
        return (
          <QESSelect {...inputProps}>
            {Object.entries(SpecialtyFilterConverter).map((entry) => {
              return context.availableFilterCriteria?.availableSpecialtyFilters.includes(entry[0]) && <option value={entry[0]} key={entry[0]}>{entry[1]}</option>;
            })}
          </QESSelect>
        );
      case "Analysis":
        return (
          <QESSelect {...inputProps}>
            {Object.entries(AnalysisTypeConverter).map((entry) => (
              context.availableFilterCriteria?.availableAnalysisTypes.includes(entry[0]) && <option value={entry[0]} key={entry[0]}>{entry[1]}</option>)
            )}
          </QESSelect>
        );
      case "Status":
        return (
          <QESSelect {...inputProps}>
            {Object.entries(ReportStatusConverter).map((entry) => (
              context.availableFilterCriteria?.availableReportStatuses.includes(entry[0]) && <option value={entry[0]} key={entry[0]}>{entry[1]}</option>)
            )}
          </QESSelect>
        );
      default:
        return null;
    }
  };

  const routeToVolumeReporting = (reportType: string) => {
    props.history.push(`${props.match.path}/create-volume-report/${reportType}`);
  }

  const getReportCount = (tab: ActiveReportTab) => {
    switch (tab) {
      case ActiveReportTab.YourReports:
        return context.reportsToday.length + context.reportsLast7.length + context.reportsLast30.length;
      case ActiveReportTab.SharedReports:
        return context.sharedReportsToday.length + context.sharedReportsLast7.length + context.sharedReportsLast30.length;
      default:
        return 0;
    }
  }

  const renderPane = (tab: ActiveReportTab) => {
    return (
      <li className="nav-item">
        <a href="#"
          role="tab"
          className={activeReportTab === tab ? "nav-link active" : "nav-link"}
          onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => setActiveReportTab(tab))}
        >
          {tab} ({getReportCount(tab)})
        </a>
      </li>
    );
  }

  const getPciVolumeReportingStatus = (): void => {
    resourceFiltrationService.getVolumeReportingProjects([], true, false, true, false, false).then((projects) => {
      if (projects?.length > 0) {
        setShowPciVolumeReportingOption(true);
      }
    });
  }

  const onCreatePciReportClick = (): void => {
    props.history.push(`${props.match.path}/create-volume-report/pci`);
  }

  const volumeReportOptions = useMemo(() => {
    const cciioReports: JSX.Element[] = [<div key="title" className="new-action-menu__subtitle">CCIIO Reports</div>];
    const volumeReports: JSX.Element[] = [<div key="title" className="new-action-menu__subtitle">Volume Reports</div>];
    const pciReports: JSX.Element[] = [<div key="title" className="new-action-menu__subtitle">PCI Reports</div>];

    if (userInfo.roles.includes(Roles.CCIIO_QHP_NETWORK_REPORTER)) {
      cciioReports.push(
        <NewActionMenuItem
          key="cciio-qhp"
          display="CCIIO QHP Download"
          testId="reports__cciio-qhp-download"
          onClick={() => routeToVolumeReporting("cciio")}
        />
      );
    }

    if (userInfo.roles.includes(Roles.CCIIO_VALIDATIONS_REPORT_USER) && featureFlags.has(Features.CCIIO_PROVIDER_VALIDATIONS)) {
      cciioReports.push(
        <NewActionMenuItem
          key="cciio-data-validations"
          display="CCIIO Data Validations"
          testId="reports__cciio-data-validations"
          onClick={() => routeToVolumeReporting("cciioDataValidations")}
        />,
        <NewActionMenuItem
          key="cciio-data-validations-summary"
          display="CCIIO Data Validations Summary"
          testId="reports__cciio-data-validations-summary"
          onClick={() => routeToVolumeReporting("cciioDataValidationsSummary")}
        />
      );
    }

    if (userInfo.roles.includes(Roles.VOLUME_REPORTER)) {
      volumeReports.push(...[
        <NewActionMenuItem
          display="County Aggregate"
          key="countyaggregate"
          onClick={() => routeToVolumeReporting("countyaggregate")}
          testId="reports__volume-report-option--countyaggregate"
        />,
        <NewActionMenuItem
          display="State Aggregate"
          key="stateaggregate"
          onClick={() => routeToVolumeReporting("stateaggregate")}
          testId="reports__volume-report-option--stateaggregate"
        />,
        <NewActionMenuItem
          display="Specialty Aggregate"
          key="specialtyaggregate"
          onClick={() => routeToVolumeReporting("specialtyaggregate")}
          testId="reports__volume-report-option--specialtyaggregate"
        />,
        <NewActionMenuItem
          display="Specialty Summary By County"
          key="specialty"
          onClick={() => routeToVolumeReporting("specialty")}
          testId="reports__volume-report-option--specialty"
        />,
        <NewActionMenuItem
          display="Providers"
          key="providers"
          onClick={() => routeToVolumeReporting("providers")}
          testId="reports__volume-report-option--providers"
        />,
        <NewActionMenuItem
          display="Zip Code Summary"
          key="zip-summary"
          onClick={() => routeToVolumeReporting("zip-summary")}
          testId="reports__volume-report-option--zip-summary"
        />
      ]);
    }

    if (userInfo.roles.includes(Roles.PROVIDER_CLAIMS_INSIGHTS) && showPciVolumeReportingOption) {
      pciReports.push(
        <NewActionMenuItem
          display="Provider Claims Insights"
          key="pci-report"
          onClick={onCreatePciReportClick}
          testId="reports__create-pci-report"
        />
      )
    }

    return compact([
      cciioReports.length > 1 ? [...cciioReports] : null,
      volumeReports.length > 1 ? [...volumeReports] : null,
      pciReports.length > 1 ? [...pciReports] : null,
    ]);
  }, [showPciVolumeReportingOption, userInfo.roles]);

  return (
    <Page documentTitle="Reports">
      <Breadcrumbs items={["Downloads"]} />
      <PageInner>
        <PageHeader title="Reports" disableHR={false}>
          <PageHeaderRight>
            {volumeReportOptions.length > 0 && (
              <NewActionMenu
                alignRight={true}
                buttonClassNames="qes-button-primary ml-3"
                buttonText="Create Report"
                disableMaxHeight={true}
                testId="reports__volume-report-dropdown"
              >
                {volumeReportOptions}
              </NewActionMenu>
            )}
          </PageHeaderRight>
        </PageHeader>
        <PageSection>
          {!context.hasLoadedToday && <LoadingSpinner />}
          {context.hasLoadedToday && (
            <div className="container mt-3 ml-0">
              <div className="row align-items-end">
                <QESSelect allowEmpty={true} containerClassName="col-4" label="Search By:" testId="reports__search-type" value={context.searchType} onChange={context.handleSearchTypeChange} disabled={!context.availableFilterCriteria}>
                  <option value="As Of">As Of</option>
                  <option value="Last Requested">Last Requested</option>
                  <option value="ProjectName">Project Name</option>
                  <option value="Project Type">Project Type</option>
                  <option value="ReportName">Report Name</option>
                  <option value="Format">Format</option>
                  <option value="Provider Category">Provider Category</option>
                  <option value="Specialty Filter">Specialty Filter</option>
                  <option value="Analysis">Analysis</option>
                  <option value="Status">Status</option>
                </QESSelect>
                {searchValueField()}
                {/*<QESButton disabled={!context.availableFilterCriteria || context.searching} qesType="secondary" qesTestId="reports__search" onClick={context.handleSearch}>{context.searching ? <LoadingSpinner /> : "Search"}</QESButton>*/}
                {context.searching && <LoadingSpinner />}
              </div>
              <div className="form-horizontal">
                <div className="row">
                  <div className="col-12">
                    <div className="tabs-style-2 mb-3">
                      <ul className="nav nav-tabs">
                        {renderPane(ActiveReportTab.YourReports)}
                        {renderPane(ActiveReportTab.SharedReports)}
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  {activeReportTab === ActiveReportTab.YourReports &&
                    <>
                      {<ReportsPanel title="Today" defaultOpen={true} reports={context.reportsToday} hasLoaded={context.hasLoadedToday} refreshReports={context.fetchToday} isYourReports={true} />}

                      {<ReportsPanel title="Last 7 Days" reports={context.reportsLast7} onOpen={context.fetchLast7} hasLoaded={context.hasLoadedLast7} refreshReports={context.fetchLast7} isYourReports={true} />}

                      {<ReportsPanel title="Last 30 Days" reports={context.reportsLast30} onOpen={context.fetchLast30} hasLoaded={context.hasLoadedLast30} refreshReports={context.fetchLast30} isYourReports={true} />}
                    </>}
                  {activeReportTab === ActiveReportTab.SharedReports &&
                    <>
                      {<ReportsPanel title="Today" defaultOpen={true} reports={context.sharedReportsToday} hasLoaded={context.hasLoadedSharedToday} refreshReports={context.fetchSharedToday} isYourReports={false} />}

                      {<ReportsPanel title="Last 7 Days" reports={context.sharedReportsLast7} onOpen={context.fetchSharedLast7} hasLoaded={context.hasLoadedSharedLast7} refreshReports={context.fetchSharedLast7} isYourReports={false} />}

                      {<ReportsPanel title="Last 30 Days" reports={context.sharedReportsLast30} onOpen={context.fetchSharedLast30} hasLoaded={context.hasLoadedSharedLast30} refreshReports={context.fetchSharedLast30} isYourReports={false} />}
                    </>}
                </div>
              </div>
            </div>
          )}
        </PageSection>
      </PageInner>
    </Page>
  );
}

const WrappedReports: FC<React.PropsWithChildren<RouteComponentProps>> = (props: RouteComponentProps) => (
  <ReportsContextProvider>
    <Reports {...props} />
  </ReportsContextProvider>
);

export { WrappedReports as Reports };
